import { api_fetch, HttpError, HttpResponse } from "./client"
import { Result } from "@/utils"

export interface Integration {
  id: string
  name: string
  username: string
  // database schema
  schema_name: string
  data: Record<string, unknown>
  created_at: string
  session_updated_at: string | null
  credential_updated_at: string
  enabled: boolean
}

export interface IntegrationWithJobInfo extends Integration {
  secondary: boolean
  syncs_via_email: boolean
  /** Whether this connection runs a scheduled sync of its own, rather than riding the mailbox's. */
  owns_sync_job: boolean
  last_sync_job?: LastSyncJobInfo
}

export interface LastSyncJobInfo {
  id: string
  created_at: string
  result?: any
  error?: string
}

// Config types for different integrations
/** Walmart settings (`WalmartConfig`). */
export interface WalmartConfig {
  /** GL account number Walmart's off-invoice allowance is written off to at cash application. */
  allowance_account?: string | null
  /** The allowance as a percentage of the invoice total. No default — it is the brand's own agreement. */
  allowance_percentage?: number | null
}

export interface KeheConfig {
  discount_term: number | null
  /** Early-pay discount rate as a percentage (the "1" in "1% 15"); server assumes 2 when unset. */
  discount_percentage?: number | null
  /** GL account number an Invoice Adjustment carrying a product/price variance codes to. */
  ia_variance_account?: string | null
  /** GL account number an allowance-only Invoice Adjustment codes to. */
  ia_spoilage_allowance_account?: string | null
  /** GL account number an off-invoice/billback-only Invoice Adjustment codes to. */
  ia_off_invoice_account?: string | null
  /** GL account number a billing/marketing-allowance-only Invoice Adjustment codes to. */
  ia_allowance_account?: string | null
  use_spoilage_allowance: boolean
  bi_fee_proactive_discount?: boolean
  bi_fee_percentage?: number | null
  /** GL account number KeHE's BI allowance short-pay is written off to at cash application. */
  bi_allowance_account?: string | null
  /** BI allowance rate as a percentage of the invoice total; the server assumes 2 when unset. */
  bi_allowance_percentage?: number | null
  aap_fee_proactive_discount?: boolean
  aap_fee_percentage?: number | null
  delivery_method?: string | null
  email_address?: string | null
  dispute_from_full_name?: string | null
}

export interface KeheUdrStatus {
  email_address: string | null
  dispute_from_full_name: string | null
  email_integration_connected: boolean
  email_integrations_count: number
  last_udr_received_at: string | null
  last_request_sent_at: string | null
  last_request_inbound_at: string | null
  receiving_udr: boolean
}

export interface KeheUdrDeduction {
  id: string
  po_number: string | null
  invoice_number: string
  invoice_amount: number
  created_at: string
  backup_id: string | null
  quantity: number | null
  unit_amount: number | null
  total: number | null
  udr_pdf_s3_uri: string | null
}

export interface UnfiConfig {
  email_address: string
  vendor_name?: string | null
  remit_number?: string | null
  discount_term?: number | null
  dispute_from_full_name?: string | null
  use_spoilage_allowance?: boolean
  ssa_has_agreement?: boolean
  ssa_signed_date?: string | null
  ssa_proactive_discount?: boolean
  ssa_invoice_start_date?: string | null
  ssa_discount_percentage?: number | null
  clearvue_has_agreement?: boolean
  clearvue_signed_date?: string | null
  clearvue_proactive_discount?: boolean
  clearvue_invoice_start_date?: string | null
  clearvue_discount_percentage?: number | null
  delivery_method?: string | null
}

export interface AholdConfig {
  vendor_number?: string
}

export interface SageIntacctConfig {
  /** Multi-entity location id applied to credit-memo lines that don't have their own. */
  credit_memo_location_id?: string | null
  /**
   * GL account number of the open-deductions holding account. When set, cash application parks
   * deductions on this account (per `open_deductions_scope`) and the Open Deductions screen
   * clears batches out of it later.
   */
  open_deductions_account_id?: string | null
  /**
   * Which deductions park in the holding account: "all_deductions" (review-first, the default) or
   * "uncoded_only" (coded deductions post straight to their GL accounts at apply time).
   */
  open_deductions_scope?: "all_deductions" | "uncoded_only" | "by_stage" | null
  /**
   * How parked deductions are laid out on the credit memo: "per_deduction" (one holding-account
   * line each, the default) or "combined" (one line for the whole memo).
   */
  open_deductions_line_grouping?: "per_deduction" | "combined" | null
  /** Sage journal symbol (e.g. GJ) the open-deductions reclassification entries post to. */
  gl_journal_id?: string | null
  /** Floor for the first invoice/payment sync (ISO date) — Sage's API is metered. */
  sync_start_date?: string | null
}

export interface SageLocation {
  id: string
  name?: string | null
}

/**
 * List the company's Sage Intacct locations (id + display name) for the credit-memo location picker.
 */
export async function getSageLocations(integrationId: string): Promise<Result<HttpResponse<SageLocation[]>, HttpError>> {
  return api_fetch<SageLocation[]>(`/integration/${integrationId}/sage/locations`)
}

/**
 * List the company's Sage Intacct GL journals (id + display name) for the reclassification journal
 * picker on the settings page.
 */
export async function getSageJournals(integrationId: string): Promise<Result<HttpResponse<SageLocation[]>, HttpError>> {
  return api_fetch<SageLocation[]>(`/integration/${integrationId}/sage/journals`)
}

export interface NetsuiteConfig {
  /** Location internal id applied to credit memos; required when NetSuite makes locations mandatory. */
  credit_memo_location_id?: string | null
  /** How early-pay discounts reach NetSuite: on the payment ("Disc. Taken"), the credit memo, or by each invoice's terms. */
  early_pay_discount_mode?: "payment_discount" | "credit_memo" | "by_invoice_terms" | null
  /** GL account internal id of the trade-spend clearing account used by the per-check month-end JE. */
  clearing_account_remote_id?: string | null
  /** No longer read: the A/R account of the A/R-to-A/R journal a multi-customer cash application used to post. */
  ar_account_remote_id?: string | null
  /** Inter-customer clearing account internal id: the dummy invoice + dummy credit memo of a multi-customer check. */
  inter_customer_clearing_account_remote_id?: string | null
  /**
   * GL account number of the open-deductions holding account. When set, cash application parks
   * deductions on this account (per `open_deductions_scope`) and the Open Deductions screen syncs
   * journal entries out of it once deductions are closed out.
   */
  open_deductions_account_id?: string | null
  /** Defaults to "by_stage" for NetSuite: the stage decides what posts directly and what parks. */
  open_deductions_scope?: "all_deductions" | "uncoded_only" | "by_stage" | null
}

export interface NetsuiteLocation {
  id: string
  name?: string | null
}

/**
 * List the account's active NetSuite locations (internal id + display name) for the credit-memo
 * location picker. Required when NetSuite's "Make Locations Mandatory" preference is on.
 */
export async function getNetsuiteLocations(integrationId: string): Promise<Result<HttpResponse<NetsuiteLocation[]>, HttpError>> {
  return api_fetch<NetsuiteLocation[]>(`/integration/${integrationId}/netsuite/locations`)
}

export interface WakefernConfig {
  dispute_from_email?: string | null
  dispute_from_name?: string | null
  discount_term?: number | null
  /** Wakefern A/P vendor numbers this brand's checks are booked under; set by the sync or staff. */
  vendor_numbers?: string[]
}

/** Config on the `cands` integration; drives batched UCD (early pay) claim files. */
export interface CandsConfig {
  dispute_from_email?: string | null
  dispute_from_name?: string | null
  vendor_number?: string | null
  discount_term?: number | null
  discount_percentage?: number | null
}

// Create integration request types
export interface CreateIntegration {
  name: string
  username: string
  password?: string
}

/**
 * Get all integrations for the current organization
 */
interface GetIntegrations {
  name: string
}
export async function getIntegrations(
  filter?: GetIntegrations
): Promise<Result<HttpResponse<IntegrationWithJobInfo[]>, HttpError>> {
  let params = filter ? filter : {}
  return api_fetch<IntegrationWithJobInfo[]>("/integration", { params })
}

/**
 * Get all integrations with their last sync job info
 */
export async function getIntegrationsWithJobs(): Promise<Result<HttpResponse<IntegrationWithJobInfo[]>, HttpError>> {
  return api_fetch<IntegrationWithJobInfo[]>("/organization/integrations/with-jobs")
}

/**
 * Get a specific integration by ID
 */
export async function getIntegration(integrationId: string): Promise<Result<HttpResponse<Integration>, HttpError>> {
  return api_fetch(`/integration/${integrationId}`)
}

/**
 * Get the stored portal password for an integration (org-scoped).
 */
export async function getIntegrationPassword(
  integrationId: string
): Promise<Result<HttpResponse<{ password: string | null }>, HttpError>> {
  return api_fetch<{ password: string | null }>(`/integration/${integrationId}/password`)
}

/**
 * Update an integration's data/config
 */
export async function updateIntegration(
  integrationId: string,
  updates: Record<string, unknown>
): Promise<Result<HttpResponse<Integration>, HttpError>> {
  return api_fetch<Integration>(`/integration/${integrationId}`, {
    method: "POST",
    body: updates,
  })
}

/**
 * Delete an integration
 */
export async function deleteIntegration(integrationId: string): Promise<Result<HttpResponse<void>, HttpError>> {
  return api_fetch(`/integration/${integrationId}`, {
    method: "DELETE",
  })
}

export type Credential = { password: string; two_fa_phone?: string } | {}

export async function upsertIntegration(
  name: string,
  username: string,
  credential: Record<string, unknown> = {}
): Promise<Result<HttpResponse<Integration>, HttpError>> {
  return api_fetch("/integration", {
    method: "POST",
    body: {
      name,
      username,
      credential,
    },
  })
}

/**
 * Trigger an immediate manual sync for an accounting integration.
 */
export async function triggerSync(integrationId: string): Promise<Result<HttpResponse<void>, HttpError>> {
  return api_fetch<void>(`/integration/${integrationId}/sync`, { method: "POST", body: {} })
}

/** One connected mailbox that already holds Southern Glazer's remittance-advice emails. */
export interface SgwsMailboxHint {
  integration_id: string
  username: string
  remittance_count: number
  latest_at: string | null
  suggested: boolean
}

/**
 * Which already-connected Gmail/Microsoft mailboxes are receiving SGWS "Electronic Remi. Advice"
 * mail. The ingest reads every mailbox; this is a suggestion of which existing connection is the
 * one receiving the docs, not a filter.
 */
export async function getSouthernGlazerMailboxHints(): Promise<Result<HttpResponse<SgwsMailboxHint[]>, HttpError>> {
  return api_fetch<SgwsMailboxHint[]>("/integration/southern-glazer/mailbox-hints")
}

/**
 * Total DB counts for accounting integration synced data.
 * Always accurate — reflects cumulative totals, not just the last sync delta.
 */
export interface AccountingIntegrationStats {
  gl_accounts: number
  invoices: number
  checks: number
}

/** @deprecated Use AccountingIntegrationStats */
export type SageIntacctStats = AccountingIntegrationStats

export async function getAccountingIntegrationStats(
  integrationId: string
): Promise<Result<HttpResponse<AccountingIntegrationStats>, HttpError>> {
  return api_fetch<AccountingIntegrationStats>(`/integration/${integrationId}/stats`)
}

/** @deprecated Use getAccountingIntegrationStats */
export const getSageStats = getAccountingIntegrationStats

export const ACCOUNTING_INTEGRATION_NAMES = ["quickbooks", "xero", "sageintacct", "netsuite"] as const

export type AccountingIntegrationName = (typeof ACCOUNTING_INTEGRATION_NAMES)[number]

export function isAccountingIntegrationName(name: string): name is AccountingIntegrationName {
  return (ACCOUNTING_INTEGRATION_NAMES as readonly string[]).includes(name)
}

export function findAccountingIntegration(
  integrations: IntegrationWithJobInfo[]
): IntegrationWithJobInfo | undefined {
  return integrations.find(i => isAccountingIntegrationName(i.name))
}

// OAuth-related functions

/**
 * Get OAuth authorization URL for email integrations (Gmail/Microsoft)
 */
export async function getOauthUrl(
  service: "gmail" | "microsoft" | "sageintacct" | "xero" | "quickbooks" | "googledrive",
  next: string
): Promise<Result<HttpResponse<string>, HttpError>> {
  return api_fetch<string>(`/${service}/oauth/start`, {
    method: "POST",
    body: { next },
  })
}

/** What a brand types into the Sage Intacct connect form. */
export interface ConnectSageIntacctRequest {
  /** The Company ID as typed at Sage's own login screen, not the company's display name. */
  company_id: string
  /** The Web Services user paired with MarginWiz's Client ID under Authorized Client Applications. */
  web_services_user_id: string
  /** Only for multi-entity companies; omitted connects at the top level. */
  entity_id?: string
}

export interface ConnectSageIntacctResponse {
  /** The `userId@companyId` we authenticated as. */
  username: string
  /** GL accounts read by the server's test query — proof the connection and its role both work. */
  gl_account_count: number
}

/**
 * Connect Sage Intacct with the client credentials grant. Unlike the OAuth integrations there is no
 * redirect: the server mints a token and reads the chart of accounts, and only stores the connection
 * if that succeeds. A failure here carries the Sage setup step that still needs changing.
 */
export async function connectSageIntacct(
  body: ConnectSageIntacctRequest
): Promise<Result<HttpResponse<ConnectSageIntacctResponse>, HttpError>> {
  return api_fetch<ConnectSageIntacctResponse>("/sageintacct/connect", {
    method: "POST",
    body,
  })
}

/** One Xero organization the connection authorized, and whether it's the currently bound one. */
export interface XeroTenantOption {
  tenant_id: string
  tenant_name: string
  selected: boolean
}

export interface XeroTenantsResponse {
  tenants: XeroTenantOption[]
  /** True while the connection has multiple organizations and none is selected yet. */
  needs_selection: boolean
}

/** List the Xero organizations this org's connection authorized, marking the bound one. */
export async function getXeroTenants(): Promise<Result<HttpResponse<XeroTenantsResponse>, HttpError>> {
  return api_fetch<XeroTenantsResponse>("/xero/tenants")
}

/** Bind this org's Xero integration to the chosen organization. */
export async function selectXeroTenant(tenant_id: string): Promise<Result<HttpResponse<unknown>, HttpError>> {
  return api_fetch<unknown>("/xero/select-tenant", {
    method: "POST",
    body: { tenant_id },
  })
}

/**
 * Get the NetSuite OAuth authorization URL. NetSuite is account-scoped and each brand registers its
 * own integration record, so the account ID plus that record's OAuth client ID/secret are supplied
 * up front. The secret is persisted server-side and never travels in the OAuth redirect URL.
 */
export async function getNetsuiteOauthUrl(
  params: { account_id: string; client_id: string; client_secret: string },
  next: string
): Promise<Result<HttpResponse<string>, HttpError>> {
  return api_fetch<string>(`/netsuite/oauth/start`, {
    method: "POST",
    body: { next, ...params },
  })
}

export interface NetsuiteM2mStartResponse {
  /** The X.509 certificate PEM the brand uploads in NetSuite's M2M setup. */
  certificate_pem: string
}

/**
 * Step 1 of the NetSuite M2M (client-credentials) connect: the server generates the signing keypair
 * and returns the certificate to upload in NetSuite. No refresh token is involved, so the connection
 * doesn't expire the way the authorization-code flow does.
 */
export async function startNetsuiteM2m(params: {
  account_id: string
  client_id: string
}): Promise<Result<HttpResponse<NetsuiteM2mStartResponse>, HttpError>> {
  return api_fetch<NetsuiteM2mStartResponse>(`/netsuite/m2m/start`, {
    method: "POST",
    body: params,
  })
}

/** Step 2: submit the Certificate ID NetSuite returned after the brand uploaded the certificate. */
export async function finalizeNetsuiteM2m(params: {
  certificate_id: string
}): Promise<Result<HttpResponse<void>, HttpError>> {
  return api_fetch<void>(`/netsuite/m2m/finalize`, {
    method: "POST",
    body: params,
  })
}

// Helper functions for finding integrations by type

/**
 * Find an integration by name from a list
 */
export function findIntegrationByName(
  integrations: IntegrationWithJobInfo[],
  name: string
): IntegrationWithJobInfo | undefined {
  const normalized = decodeURIComponent(name)
  return integrations.find(i => i.name === normalized)
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("isAccountingIntegrationName", () => {
    it("includes supported accounting integrations", () => {
      expect(isAccountingIntegrationName("quickbooks")).toBe(true)
      expect(isAccountingIntegrationName("xero")).toBe(true)
      expect(isAccountingIntegrationName("sageintacct")).toBe(true)
      expect(isAccountingIntegrationName("netsuite")).toBe(true)
    })

    it("excludes non-accounting integrations", () => {
      expect(isAccountingIntegrationName("kehe")).toBe(false)
    })
  })
}

/**
 * Find an integration by id from a list
 */
export function findIntegrationById(
  integrations: IntegrationWithJobInfo[],
  id: string
): IntegrationWithJobInfo | undefined {
  return integrations.find(i => i.id === id)
}

/**
 * Find the email integration matching a username
 */
export function findEmailIntegrationForUsername(
  integrations: IntegrationWithJobInfo[],
  username: string
): IntegrationWithJobInfo | undefined {
  // Case-insensitive: providers report the same mailbox with different casing over time.
  const wanted = username.trim().toLowerCase()
  return integrations.find(
    i => (i.name === "gmail" || i.name === "microsoft") && i.username.trim().toLowerCase() === wanted
  )
}
